/**
 * Cloud upload receipt — the honest record of whether a run reached the platform.
 *
 * `summary.json` has always carried `testRunId`, and consumers (TestRelic Studio
 * among them) read its presence as proof the run was uploaded. It is not proof:
 * `testRunId` is `config.testRunId ?? randomUUID()`, generated locally in `onBegin`
 * and written on every run — including a fully offline one, which then renders as
 * "Uploaded" behind a dashboard link that 404s.
 *
 * The platform does not mint run ids either — `POST /runs/init` and `POST /runs`
 * both file the run under the id the SDK sent — so there is no server-side id to
 * record instead. What the SDK *does* know, and never wrote down, is whether the
 * platform acknowledged the run. That is what `cloudUpload` carries.
 *
 * The block is written on EVERY run, so a consumer can distinguish three states:
 *   - block absent        → an SDK too old to report a verdict; treat as unknown
 *   - `acknowledged:false` → the run did not reach the platform, and `detail` says why
 *   - `acknowledged:true`  → the platform accepted the run under `runId`
 *
 * It is written twice per run, both times atomically (tmp + rename): once with the
 * pre-upload state, and again once the upload settles. A process killed between the
 * two leaves the earlier, more conservative verdict on disk — this block under-claims
 * when it is wrong, never over-claims.
 */

import type { CloudClient } from './cloud-client.js';

/** Upload strategies the reporter actually acts on. Anything else uploads nothing. */
const KNOWN_STRATEGIES: ReadonlySet<string> = new Set(['batch', 'realtime', 'both']);

/**
 * Whether the platform acknowledged this run, and what it did with it.
 * Written to `summary.json` as `cloudUpload` on every run.
 */
export interface CloudUploadReceipt {
  /** Cloud mode was on and a strategy the reporter acts on was selected. */
  readonly attempted: boolean;
  /** The platform accepted the run — realtime `/runs/init`, or the batch `POST /runs`. */
  readonly acknowledged: boolean;
  /** The id the run was filed under on the platform, or null when never acknowledged. */
  readonly runId: string | null;
  /** The ingest endpoint the run was sent to, or null when nothing was sent. */
  readonly endpoint: string | null;
  /** The run was closed out — realtime finalize returned ok, or the batch upload landed. */
  readonly finalized: boolean;
  /** Artifacts the platform confirmed it stored. */
  readonly artifactsUploaded: number;
  /** Artifacts offered for upload. */
  readonly artifactsTotal: number;
  /** Short plain reason when the run was not acknowledged. Null once it was. */
  readonly detail: string | null;
}

/** A streaming summary carrying the upload receipt. */
export type WithCloudUpload<T> = T & { readonly cloudUpload: CloudUploadReceipt };

/** What `finalizeAndUpload` observed, so the reporter can compose the final receipt. */
export interface CloudFinalizeOutcome {
  /** A realtime finalize POST was issued (strategy realtime/both, run was initialised). */
  readonly realtimeFinalizeAttempted: boolean;
  /** That finalize POST returned ok. */
  readonly realtimeFinalized: boolean;
  /** A batch `POST /runs` was issued (strategy batch/both). */
  readonly batchAttempted: boolean;
  /** That batch POST landed (2xx, or 409 duplicate — the data is already ingested). */
  readonly batchAcknowledged: boolean;
  /** Artifacts offered for upload. */
  readonly artifactsTotal: number;
  /** Artifacts the platform confirmed it stored. */
  readonly artifactsUploaded: number;
  /** Short machine reason for the first thing that failed, if anything did. */
  readonly failureReason: string | null;
}

/** The receipt for a run where nothing was ever wired up to the cloud. */
export const UNATTEMPTED_RECEIPT: CloudUploadReceipt = Object.freeze({
  attempted: false,
  acknowledged: false,
  runId: null,
  endpoint: null,
  finalized: false,
  artifactsUploaded: 0,
  artifactsTotal: 0,
  detail: 'cloud reporting was never initialised',
});

/** Translate a CloudClient failure reason into one short plain sentence. */
function describeLocalMode(reason: string | null): string {
  switch (reason) {
    case 'no_api_key':
      return 'cloud mode off — no API key configured';
    case 'cloud_unreachable':
    case 'network_error':
      return 'cloud endpoint unreachable — the run stayed local';
    case 'invalid_api_key':
      return 'API key rejected as invalid or revoked — the run stayed local';
    case 'expired_api_key':
      return 'API key has expired — the run stayed local';
    case 'validation_error':
      return 'API key format rejected — the run stayed local';
    case 'rate_limited':
      return 'rate limited during authentication — the run stayed local';
    case 'auth_timeout':
      return 'authentication timed out — the run stayed local';
    case 'token_expired_no_refresh':
    case 'token_refresh_failed':
    case 'token_refresh_error':
      return 'access token could not be refreshed — the run stayed local';
    case 'init_error':
      return 'cloud initialisation timed out — the run stayed local';
    case 'unexpected_error':
      return 'cloud initialisation failed — the run stayed local';
    default:
      return reason
        ? `cloud mode off (${reason}) — the run stayed local`
        : 'cloud mode off — the run stayed local';
  }
}

/**
 * Build the receipt as it stands right after `onBegin` — before any finalize or
 * batch upload has run. This is the verdict a crashed or interrupted run carries.
 */
export function buildInitialReceipt(
  cloudClient: CloudClient | null,
  uploadStrategy: string | undefined,
  cloudRunId: string | null,
): CloudUploadReceipt {
  if (!cloudClient) return UNATTEMPTED_RECEIPT;

  if (!cloudClient.isCloudMode()) {
    return {
      ...UNATTEMPTED_RECEIPT,
      detail: describeLocalMode(cloudClient.getFailureReason()),
    };
  }

  // `isValidCloudConfig` rejects an unknown strategy upstream (the whole cloud block
  // falls back to defaults), so this should be unreachable through config. It is kept
  // because `finalizeAndUpload` takes `uploadStrategy: string | undefined` from any
  // caller, and an unrecognised value matches neither of its branches — the run then
  // uploads nothing at all, silently. Name that rather than imply it shipped.
  const strategy = uploadStrategy ?? 'batch';
  if (!KNOWN_STRATEGIES.has(strategy)) {
    return {
      ...UNATTEMPTED_RECEIPT,
      detail: `upload strategy '${strategy}' is not recognised — nothing was sent`,
    };
  }

  const endpoint = cloudClient.getEndpoint();
  const isRealtime = strategy === 'realtime' || strategy === 'both';

  if (isRealtime && cloudRunId) {
    return {
      attempted: true,
      acknowledged: true,
      runId: cloudRunId,
      endpoint,
      finalized: false,
      artifactsUploaded: 0,
      artifactsTotal: 0,
      detail: null,
    };
  }

  return {
    attempted: true,
    acknowledged: false,
    runId: null,
    endpoint,
    finalized: false,
    artifactsUploaded: 0,
    artifactsTotal: 0,
    detail: isRealtime
      ? 'run init failed — the platform did not acknowledge this run'
      : `upload runs at the end of the run under the '${strategy}' strategy — not sent yet`,
  };
}

/**
 * Fold what the upload phase observed into the receipt.
 *
 * `testRunId` is the id the SDK filed the run under; it becomes `runId` only once the
 * platform has actually answered, so an unacknowledged run still reports `runId: null`.
 */
export function applyFinalizeOutcome(
  receipt: CloudUploadReceipt,
  outcome: CloudFinalizeOutcome | undefined,
  testRunId: string,
): CloudUploadReceipt {
  if (!receipt.attempted) return receipt;

  if (!outcome) {
    return {
      ...receipt,
      detail: receipt.acknowledged
        ? 'upload timed out before it completed — the run may be incomplete on the platform'
        : 'upload timed out before the platform acknowledged this run',
      acknowledged: receipt.acknowledged,
    };
  }

  const acknowledged = receipt.acknowledged || outcome.batchAcknowledged;
  const runId = receipt.runId ?? (outcome.batchAcknowledged ? testRunId : null);

  // "Closed out" means different things per strategy: realtime has an explicit finalize
  // POST, while a batch upload IS the whole run — landing it closes the run by itself.
  const finalized = outcome.realtimeFinalizeAttempted
    ? outcome.realtimeFinalized
    : outcome.batchAcknowledged;

  let detail: string | null = null;
  if (!acknowledged) {
    detail = outcome.failureReason
      ? `upload failed (${outcome.failureReason}) — queued locally for retry`
      : 'upload failed — the platform did not acknowledge this run';
  } else if (!finalized) {
    detail = 'run acknowledged but not closed out — finalize failed and was queued for retry';
  }

  return {
    attempted: true,
    acknowledged,
    runId,
    endpoint: receipt.endpoint,
    finalized,
    artifactsUploaded: outcome.artifactsUploaded,
    artifactsTotal: outcome.artifactsTotal,
    detail,
  };
}
